import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './GallerySection.css';

function GallerySection() {
  const [isDevilTrigger, setIsDevilTrigger] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const toggleForm = () => {
    setIsDevilTrigger(!isDevilTrigger);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <section className="gallery-section" id="gallery">
      <div className="gallery-container">
        <div className="section-header">
          <motion.span 
            className="section-tag"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="visual-icon gallery-icon"></span> GALLERY
          </motion.span>
          <motion.h2 
            className="section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            DANTE FORMS
          </motion.h2>
          <p className="section-subtitle">Click on Dante to unleash the Devil Trigger</p>
        </div>

        <div className="gallery-content">
          {/* Main Toggle Card */}
          <motion.div 
            className="dante-showcase"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <div className="form-toggle">
              <button 
                className={`toggle-btn ${!isDevilTrigger ? 'active' : ''}`}
                onClick={() => setIsDevilTrigger(false)}
              >
                HUMAN
              </button>
              <button 
                className={`toggle-btn ${isDevilTrigger ? 'active' : ''}`}
                onClick={() => setIsDevilTrigger(true)}
              >
                DEVIL TRIGGER
              </button>
            </div>

            <motion.div 
              className="showcase-card"
              onClick={openModal}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={isDevilTrigger ? 'devil' : 'human'}
                  className="showcase-image-container"
                  initial={{ opacity: 0, rotateY: 90 }}
                  animate={{ opacity: 1, rotateY: 0 }}
                  exit={{ opacity: 0, rotateY: -90 }}
                  transition={{ duration: 0.5 }}
                >
                  <img 
                    src={isDevilTrigger 
                      ? '/images/gallery/dante-normal.jpg' 
                      : '/images/gallery/dante-devil-trigger.png'
                    } 
                    alt={isDevilTrigger ? 'Dante Devil Trigger' : 'Dante'}
                    className="showcase-image"
                  />
                  <div className={`showcase-overlay ${isDevilTrigger ? 'devil' : 'human'}`}>
                    <span className="form-label">
                      {isDevilTrigger ? 'DEVIL TRIGGER' : 'HUMAN FORM'}
                    </span>
                  </div>
                </motion.div>
              </AnimatePresence>

              <div className="showcase-info">
                <h3 className="showcase-title">
                  {isDevilTrigger ? 'DEVIL TRIGGER' : 'DANTE'}
                </h3>
                <p className="showcase-description">
                  {isDevilTrigger 
                    ? 'Unleash your inner demon! Devil Trigger transforms Dante into his demonic form, granting increased power, speed, and regeneration. The ultimate expression of his half-demon heritage.'
                    : 'The legendary Son of Sparda. A demon hunter who runs the Devil May Cry shop. Known for his cocky attitude, love of pizza, and unmatched combat skills.'
                  }
                </p>
                <div className="showcase-stats">
                  <div className="showcase-stat">
                    <span className="stat-value">{isDevilTrigger ? '200%' : '100%'}</span>
                    <span className="stat-name">POWER</span>
                  </div>
                  <div className="showcase-stat">
                    <span className="stat-value">{isDevilTrigger ? '150%' : '100%'}</span>
                    <span className="stat-name">SPEED</span>
                  </div>
                  <div className="showcase-stat">
                    <span className="stat-value">{isDevilTrigger ? 'YES' : 'NO'}</span>
                    <span className="stat-name">REGEN</span>
                  </div>
                </div>
              </div>

              <motion.div 
                className="click-hint"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                Click to {isDevilTrigger ? 'view Human form' : 'activate Devil Trigger'}
              </motion.div>
            </motion.div>

            {/* Quick Toggle Button */}
            <motion.button 
              className="devil-trigger-btn"
              onClick={toggleForm}
              whileHover={{ scale: 1.05, boxShadow: '0 0 50px rgba(196, 30, 58, 0.8)' }}
              whileTap={{ scale: 0.95 }}
              animate={isDevilTrigger ? {
                boxShadow: ['0 0 20px rgba(196, 30, 58, 0.5)', '0 0 40px rgba(196, 30, 58, 0.8)', '0 0 20px rgba(196, 30, 58, 0.5)']
              } : {}}
              transition={{ duration: 1, repeat: isDevilTrigger ? Infinity : 0 }}
            >
              <span className={`btn-icon visual-icon ${isDevilTrigger ? 'demon-icon' : 'fire-icon'}`}></span>
              <span>{isDevilTrigger ? 'DEACTIVATE' : 'DEVIL TRIGGER'}</span>
            </motion.button>
          </motion.div>
        </div>
      </div>

      {/* Modal for full view */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div 
            className="gallery-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div 
              className="modal-content"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="modal-close" onClick={closeModal}>×</button>
              <img 
                src={isDevilTrigger 
                  ? '/images/gallery/dante-normal.jpg' 
                  : '/images/gallery/dante-devil-trigger.png'
                } 
                alt={isDevilTrigger ? 'Dante Devil Trigger' : 'Dante'}
                className="modal-image"
              />
              <div className="modal-info">
                <h3>{isDevilTrigger ? 'DEVIL TRIGGER FORM' : 'HUMAN FORM'}</h3>
                <p>Click outside to close</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

export default GallerySection;
